import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import { BuildingThreeIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'

import useTranslate from '../../../../../lib/hooks/use-translate'
import { staffQualificationEnumMapping } from '../../../../../lib/types/enums/staff/staff-qualifications.enum'
import { AffectedStaffProperties } from '../../../../../lib/types/models/affectedStaff/properties.affected-staff'
import { CenterProperties } from '../../../../../lib/types/models/center/properties.center'
import { OptionProperties } from '../../../../../lib/types/models/options/option-properties'
import { StaffRequest } from '../../../../../lib/types/models/staff-request/staff-request'
import { getTranslatedName } from '../../../../../lib/utils/functions/translation.functions'
import CountryFlag from '../../../../elements/country-flag'
import LabelWithText from '../../../ols/label-with-text'

type Props = {
  showCenter: boolean
  staffRequest: StaffRequest
}

const StaffPersonalSpaceJourneySection = ({ showCenter, staffRequest }: Props) => {
  const { t, currentLanguage } = useTranslate()

  const { days, nights } = AffectedStaffProperties.getStaffJourneyDuration(staffRequest.duration)

  const mainOptions = staffRequest.centers
    .flatMap((center) => CenterProperties.getCenterThemeName(center))
    .join(', ')

  const secondaryOptions = staffRequest.centers
    .flatMap((center) => CenterProperties.getCenterOptions(center))
    .join(', ')

  return (
    <InnerBox className="rounded-lg p-3 bg-white-hover h-fit">
      <div className="flex items-center gap-2 mobile:flex-col mobile:items-start">
        <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
          {t('staff.personal.space.my.journeys.details.tab.home.journey')}
        </Typography>
      </div>
      <div className="h-3.5" />
      <div className="flex items-center gap-3 mobile:flex-col mobile:items-start">
        <img
          src={OptionProperties.getOptionPicture(
            staffRequest.centers[0]?.centerToJourneys[0].journey.mainOption
          )}
          alt="Description"
          className="w-[98px] h-[98px] rounded-lg"
        />
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1">
            <CountryFlag
              possibleCountry={staffRequest.country?.name}
              code={staffRequest.country?.code}
            />
            <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
              {staffRequest.country?.name}
            </Typography>
          </div>
          {staffRequest.startDate && staffRequest.endDate && (
            <div className="flex items-center gap-1">
              <Badge
                color={tailwindColors.secondary.light.DEFAULT}
                textColor={tailwindColors.secondary.DEFAULT}
                className="text-nowrap"
                text={`${DateFormatter.DDMMYYYY(staffRequest.startDate)} - ${DateFormatter.DDMMYYYY(staffRequest.endDate)}`}
              />
              <Badge
                color={tailwindColors.secondary.light.DEFAULT}
                textColor={tailwindColors.secondary.DEFAULT}
                className="text-nowrap"
                text={`${days} ${t('staff.personal.space.my.journeys.details.tab.home.journey.day')} / ${nights} ${t('staff.personal.space.my.journeys.details.tab.home.journey.night')}`}
              />
            </div>
          )}
          {showCenter && (
            <Badge
              color={tailwindColors.primary.light.DEFAULT}
              text={staffRequest.centers.map((center) => center.name).join(', ')}
              iconPosition="start"
              textColor={tailwindColors.primary.DEFAULT}
              icon={<BuildingThreeIcon color={tailwindColors.primary.DEFAULT} />}
            />
          )}
        </div>
      </div>
      <div className="h-4" />
      <div className="grid grid-cols-3 mobile:grid-cols-1 mobile:gap-2 gap-8">
        <LabelWithText
          label={t('staff.personal.space.my.journeys.details.tab.home.journey.qualification')}
          data={t(staffQualificationEnumMapping[staffRequest.qualification].label)}
        />
        <LabelWithText
          label={t('staff.personal.space.my.journeys.details.tab.home.journey.themes')}
          data={mainOptions}
        />
        <LabelWithText
          label={t('staff.personal.space.my.journeys.details.tab.home.journey.options')}
          data={secondaryOptions}
        />
      </div>
      <div className="h-4" />
      <LabelWithText
        label={t('staff.personal.space.my.journeys.details.tab.home.journey.formulas')}
        data={
          <div className="flex flex-col gap-1 w-full ">
            {staffRequest.formulas.map((formula) => (
              <Badge
                className="w-full mobile:h-fit"
                key={formula.id}
                color={tailwindColors.white.active}
                borderColor={tailwindColors.grey.light.DEFAULT}
                text={
                  <Typography variant="label" color={tailwindColors.black.DEFAULT}>
                    <strong>{getTranslatedName(formula, currentLanguage) || '—'} </strong>(
                    {formula.description})
                  </Typography>
                }
              />
            ))}
          </div>
        }
      />
    </InnerBox>
  )
}

export default StaffPersonalSpaceJourneySection
